<template>
  <v-container fluid class="pa-2 fill-height">
    <v-row no-gutters>
      <!-- Liste des fichiers -->
      <v-col
        cols="3"
        class="pa-2"
        style="border-right: 1px solid #ccc; overflow-y: auto; max-height: 90vh;"
      >
        <h3 class="mb-3">AB2CD FLIGHTS</h3>

        <v-progress-circular
          v-if="loading"
          indeterminate
          color="primary"
        ></v-progress-circular>

        <v-alert v-if="error" type="error" dense>{{ error }}</v-alert>

        <v-list dense>
          <v-list-item
            v-for="(file, index) in kmlFiles"
            :key="index"
            @mouseenter="highlightKml(file)"
            @mouseleave="unhighlightKml(file)"
          >
            <v-checkbox
              hide-details
              v-model="selectedFiles"
              :label="formatLabel(file)"
              :value="file"
              @change="toggleKml(file)"
            />
          </v-list-item>
        </v-list>
      </v-col>

      <!-- Carte Leaflet -->
      <v-col cols="9" class="pa-0">
        <div ref="map" class="map"></div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import axios from "axios";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

export default {
  name: "KmlMapViewer",
  data() {
    return {
      map: null,
      kmlFiles: [],
      selectedFiles: [],
      kmlLayers: {},
      kmlColors: {},
      originalColors: {},
      loading: false,
      error: null,
      baseUrl: "https://observations.ipsl.fr/aeris/magic/qlooks/AIRCRAFT/FLIGHTS/",
      blueIcon: null
    };
  },
  async mounted() {
    this.initMap();
    this.initMarkerIcon();
    await this.loadKmlList();
  },
  methods: {
    /** Initialise la carte Leaflet */
    initMap() {
      this.map = L.map(this.$refs.map).setView([46.5, 2.5], 6);
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "© OpenStreetMap contributors"
      }).addTo(this.map);
    },

    /** Initialise l’icône bleue personnalisée */
    initMarkerIcon() {
      this.blueIcon = L.icon({
        iconUrl:
          "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-blue.png",
        shadowUrl:
          "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
        iconSize: [25, 41],
        iconAnchor: [12, 41],
        popupAnchor: [1, -34],
        shadowSize: [41, 41]
      });
    },

    /** Charge la liste des fichiers KML disponibles */
    async loadKmlList() {
      this.loading = true;
      try {
        const response = await axios.get(this.baseUrl);
        const matches = response.data.match(/href="([^"]+\.kml)"/gi) || [];
        this.kmlFiles = matches
          .map(m => m.replace(/href="|"/g, ""))
          .filter(name => !name.includes("FlightPlan"));
      } catch (err) {
        this.error = "Erreur de chargement de la liste KML.";
        console.error(err);
      } finally {
        this.loading = false;
      }
    },

    /** Formatte le nom du fichier pour affichage dans la liste */
    formatLabel(filename) {
      let label = filename;
      label = label.replace("AB2CDDISC-2025_SAFIRE-ATR42_Flight_Path_", "ATR_");
      label = label.replace(".kml", "");
      return label;
    },

    /** Active ou désactive un fichier KML */
    async toggleKml(file) {
      const url = this.baseUrl + file;

      if (this.kmlLayers[file]) {
        try {
          this.map.removeLayer(this.kmlLayers[file]);
        } catch (e) {}
        delete this.kmlLayers[file];
        delete this.kmlColors[file];
        delete this.originalColors[file];
        return;
      }

      try {
        const response = await axios.get(url);
        const parser = new DOMParser();
        const xml = parser.parseFromString(response.data, "text/xml");

        const featureGroup = L.featureGroup();
        const placemarks = xml.getElementsByTagName("Placemark");
        const color = this.randomColor();
        this.kmlColors[file] = color;

        for (let i = 0; i < placemarks.length; i++) {
          const pm = placemarks[i];
          const name = pm.getElementsByTagName("name")[0]?.textContent || "Unnamed";
          const coordsTag = pm.getElementsByTagName("coordinates")[0];
          if (!coordsTag) continue;

          const coordsList = coordsTag.textContent
            .trim()
            .split(/\s+/)
            .map(pt => pt.split(",").map(Number))
            .filter(c => c.length >= 2);

          if (coordsList.length === 1) {
            const [lon, lat] = coordsList[0];
            const marker = L.marker([lat, lon], { icon: this.blueIcon }).bindPopup(name);
            featureGroup.addLayer(marker);
          } else if (coordsList.length > 1) {
            const latlngs = coordsList.map(([lon, lat]) => [lat, lon]);
            const line = L.polyline(latlngs, {
              color,
              weight: 3,
              opacity: 0.8
            }).bindPopup(name);
            featureGroup.addLayer(line);
          }
        }

        featureGroup.addTo(this.map);
        this.kmlLayers[file] = featureGroup;

        const bounds = featureGroup.getBounds();
        if (bounds.isValid && bounds.isValid()) {
          this.map.fitBounds(bounds);
        }
      } catch (err) {
        console.error("Erreur de chargement KML", err);
        this.error = `Erreur de chargement du fichier ${file}`;
      }
    },

    highlightKml(file) {
      const featureGroup = this.kmlLayers[file];
      if (!featureGroup) return;

      this.originalColors[file] = [];
      featureGroup.eachLayer(layer => {
        if (layer.setStyle) {
          const prevColor = layer.options.color;
          this.originalColors[file].push(prevColor);
          layer.setStyle({ weight: 7 });
        }
      });

      const bounds = featureGroup.getBounds();
      if (bounds.isValid && bounds.isValid()) {
        this.map.flyToBounds(bounds, { maxZoom: 9 });
      }
    },

    unhighlightKml(file) {
      const featureGroup = this.kmlLayers[file];
      if (!featureGroup) return;

      const orig = this.originalColors[file] || [];
      let i = 0;
      featureGroup.eachLayer(layer => {
        if (layer.setStyle) {
          layer.setStyle({ color: orig[i] || this.kmlColors[file] || "blue", weight: 3 });
          i++;
        }
      });
    },

    randomColor() {
      const hue = Math.floor(Math.random() * 360);
      return `hsl(${hue}, 80%, 50%)`;
    }
  }
};
</script>

<style scoped>
.map {
  width: 100%;
  height: 90vh;
}
</style>
